import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SystemUpdateAltRoundedIcon from '@mui/icons-material/SystemUpdateAltRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { orchard } from '../../theme';
import { useAppUpdate, startUpdate, APK_URL } from './appVersion';

const DISMISS_KEY = 'cp.update.dismissed';

/* Inside the Android app: «Вышла новая версия» with an «Обновить» button,
   when /app/version.json is newer than the installed app. «Позже» hides it
   for a day. The same update is in the side menu (Layout). */
export default function AppUpdateBanner({ en }) {
  const { installed, latest, outdated } = useAppUpdate();
  const [hidden, setHidden] = useState(() => {
    try {
      const d = JSON.parse(window.localStorage.getItem(DISMISS_KEY) || 'null');
      return Boolean(d && Date.now() - d.at < 24 * 3600 * 1000);
    } catch (e) {
      return false;
    }
  });
  const [open, setOpen] = useState(false);
  if (!outdated) return null;

  const dismiss = () => {
    setHidden(true);
    try {
      window.localStorage.setItem(
        DISMISS_KEY,
        JSON.stringify({ at: Date.now() })
      );
    } catch (e) {
      // private mode
    }
  };

  return (
    <>
      {!hidden && (
        <Box
          role="status"
          sx={{
            position: 'fixed',
            left: 12,
            right: 12,
            bottom: {
              xs: 'calc(76px + env(safe-area-inset-bottom))',
              md: 'max(16px, env(safe-area-inset-bottom))',
            },
            zIndex: 1150, // above the bottom navigation (1100), under the side menu (1200)
            mx: 'auto',
            maxWidth: 520,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            p: 1.25,
            pl: 1.5,
            borderRadius: '18px',
            color: '#fff',
            background: `linear-gradient(135deg, ${orchard.cherry} 0%, ${orchard.cherryDeep} 120%)`,
            boxShadow: `0 18px 40px -16px ${alpha(orchard.cherryDeep, 0.9)}`,
          }}
        >
          <SystemUpdateAltRoundedIcon />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{ fontWeight: 700, fontSize: '0.92rem', lineHeight: 1.3 }}
            >
              {en
                ? `New app version ${latest.version}`
                : `Новая версия приложения ${latest.version}`}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', opacity: 0.85 }}>
              {en ? `You have ${installed}` : `У вас ${installed}`}
            </Typography>
          </Box>
          <Button
            onClick={() => setOpen(true)}
            variant="contained"
            sx={{
              bgcolor: '#fff',
              // the theme gives contained buttons a cherry gradient image
              backgroundImage: 'none',
              color: orchard.cherryDeep,
              fontWeight: 800,
              borderRadius: 999,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: orchard.blossom,
                backgroundImage: 'none',
                boxShadow: 'none',
              },
            }}
          >
            {en ? 'Update' : 'Обновить'}
          </Button>
          <IconButton
            size="small"
            onClick={dismiss}
            aria-label={en ? 'Later' : 'Позже'}
            sx={{ color: '#fff' }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      <UpdateDialog
        open={open}
        onClose={() => setOpen(false)}
        latest={latest}
        installed={installed}
        en={en}
      />
    </>
  );
}

export function UpdateDialog({ open, onClose, latest, installed, en }) {
  const [started, setStarted] = useState(null);
  const notes = (
    latest && Array.isArray(latest.notes) ? latest.notes : []
  ).slice(0, 6);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 800 }}>
        {en ? 'Update the app' : 'Обновить приложение'}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 1.5 }}>
          {en
            ? `Version ${latest ? latest.version : ''} is ready (you have ${installed || '—'}).`
            : `Готова версия ${latest ? latest.version : ''} (у вас ${installed || '—'}).`}
        </Typography>
        {notes.length > 0 && (
          <Box
            component="ul"
            sx={{
              m: 0,
              mb: 1.5,
              pl: 2.5,
              color: 'text.secondary',
              fontSize: '0.9rem',
            }}
          >
            {notes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </Box>
        )}
        {started ? (
          <Typography
            sx={{
              fontSize: '0.9rem',
              p: 1.25,
              borderRadius: 2,
              bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
            }}
          >
            {started === 'native'
              ? en
                ? 'Downloading… The installer opens by itself — tap «Update». If Android asks, allow installing apps from Cherry-Pick.'
                : 'Скачиваю… Установщик откроется сам — нажмите «Обновить». Если Android спросит — разрешите установку приложений из Cherry-Pick.'
              : en
                ? 'Downloading… When it finishes, tap the «CherryPickCore.apk» notification and then «Update». Your login and data stay.'
                : 'Скачиваю… Когда загрузка закончится, нажмите на уведомление «CherryPickCore.apk», затем «Обновить». Вход и данные сохранятся.'}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: '0.88rem', color: 'text.secondary' }}>
            {en
              ? 'The file comes from our site core.cherry-pick.ru. Your login and data stay.'
              : 'Файл скачивается с нашего сайта core.cherry-pick.ru. Вход и данные сохранятся.'}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{en ? 'Close' : 'Закрыть'}</Button>
        <Button
          variant="contained"
          startIcon={<SystemUpdateAltRoundedIcon />}
          onClick={() =>
            setStarted(startUpdate((latest && latest.url) || APK_URL))
          }
        >
          {started
            ? en
              ? 'Download again'
              : 'Скачать ещё раз'
            : en
              ? 'Download update'
              : 'Скачать обновление'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
